import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { BasketDiscountWriteCampaignProps } from 'apis/restaurant-api/merchant-admin/campaigns/api';
import { BasketDiscountType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { typographyCss } from 'common/typography';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useCampaignModuleContext } from 'modules/campaigns/context';

type BasketDiscountVariantTextProps = {
  discountType: BasketDiscountType;
  discountValue: BasketDiscountWriteCampaignProps['basketDiscount'];
  minBasketSize: number | undefined;
};

const Text = styled.div`
  ${typographyCss.Body3};
  font-weight: 700;
`;

const BasketDiscountVariantText = ({ discountType, discountValue, minBasketSize }: BasketDiscountVariantTextProps) => {
  const { currency } = useCampaignModuleContext();

  const { amount, percentage, upperLimit } = discountValue;

  const minBasket = (
    <MonetaryAmount
      amount={convertMonetaryIntToDecimal(minBasketSize ?? 0, currency)}
      numberFormatOptions={{ minimumFractionDigits: 0 }}
      currency={currency}
    />
  );

  if (discountType === BasketDiscountType.AMOUNT) {
    return (
      <Text>
        <FormattedMessage
          id="campaigns.sidebar-preview.basket-banner-preview.amount"
          values={{
            discountAmount: (
              <MonetaryAmount
                amount={convertMonetaryIntToDecimal(amount ?? 0, currency)}
                numberFormatOptions={{ minimumFractionDigits: 0 }}
                currency={currency}
              />
            ),
            minBasket,
          }}
        />
      </Text>
    );
  }

  if (discountType === BasketDiscountType.PERCENTAGE && upperLimit) {
    return (
      <Text>
        <FormattedMessage
          id="campaigns.sidebar-preview.basket-banner-preview.percentage-with-limit"
          values={{
            discountPercentage: percentage ?? 0,
            upperLimit: (
              <MonetaryAmount
                amount={convertMonetaryIntToDecimal(upperLimit, currency)}
                numberFormatOptions={{ minimumFractionDigits: 0 }}
                currency={currency}
              />
            ),
            minBasket,
          }}
        />
      </Text>
    );
  }

  if (discountType === BasketDiscountType.PERCENTAGE) {
    return (
      <Text>
        <FormattedMessage
          id="campaigns.sidebar-preview.basket-banner-preview.percentage"
          values={{
            discountPercentage: percentage ?? 0,
            minBasket,
          }}
        />
      </Text>
    );
  }

  return null;
};

export default BasketDiscountVariantText;
